import { useEffect, useId, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { socialPostApi } from '../../api/socialPostApi';
import { resolveSafeUserVisibleError } from '@formosa/shared/i18n/safeUserVisibleError';
import { normalizeSocialPostDetail } from '../social-post/socialPostDetailModel';
import {
  composerItemsFromReadMedia,
  hasPendingMediaUpload,
  mediaFingerprint,
} from '../social-post/socialPostMediaModel';
import {
  SOCIAL_COMPOSE_REPLY_POLICIES,
  SOCIAL_COMPOSE_REPLY_POLICY_LABELS,
  SOCIAL_COMPOSE_QUOTE_POLICIES,
  SOCIAL_COMPOSE_QUOTE_POLICY_LABELS,
  SOCIAL_COMPOSE_DEFAULT_REPLY_POLICY,
  SOCIAL_COMPOSE_DEFAULT_QUOTE_POLICY,
  SOCIAL_TEXT_MAX,
  buildEditSocialPostBody,
} from './socialComposeModel';
import SocialPostMediaEditor from './SocialPostMediaEditor';
import { SOCIAL_COMPOSE_PRODUCT_CHROME as composeChrome } from '../social-post/socialActionPresentation';
import './social-compose.css';

export default function SocialPostEditPage() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const headingId = useId();
  const textId = useId();
  const replyPolicyId = useId();
  const quotePolicyId = useId();
  const [phase, setPhase] = useState('loading');
  const [reloadToken, setReloadToken] = useState(0);
  const [textContent, setTextContent] = useState('');
  const [mediaItems, setMediaItems] = useState([]);
  const [replyPolicy, setReplyPolicy] = useState(SOCIAL_COMPOSE_DEFAULT_REPLY_POLICY);
  const [quotePolicy, setQuotePolicy] = useState(SOCIAL_COMPOSE_DEFAULT_QUOTE_POLICY);
  const [expectedRowVersion, setExpectedRowVersion] = useState(0);
  const [baseline, setBaseline] = useState({
    text: '',
    media: '',
    replyPolicy: SOCIAL_COMPOSE_DEFAULT_REPLY_POLICY,
    quotePolicy: SOCIAL_COMPOSE_DEFAULT_QUOTE_POLICY,
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setPhase('loading');
      setFormError('');
      try {
        const detail = normalizeSocialPostDetail(await socialPostApi.getDetail(postId));
        if (cancelled) return;
        const items = composerItemsFromReadMedia(detail.media);
        setTextContent(detail.text || '');
        setMediaItems(items);
        setReplyPolicy(detail.replyPolicy);
        setQuotePolicy(detail.quotePolicy);
        setExpectedRowVersion(detail.rowVersion);
        setBaseline({
          text: detail.text || '',
          media: mediaFingerprint(items),
          replyPolicy: detail.replyPolicy,
          quotePolicy: detail.quotePolicy,
        });
        setPhase('ready');
      } catch (cause) {
        if (cancelled) return;
        setFormError(resolveSafeUserVisibleError(cause, () => '無法載入貼文。', 'travel.common.loadFailed'));
        setPhase('error');
      }
    }
    if (/^[1-9]\d*$/.test(postId || '')) load();
    else {
      setFormError('找不到這則貼文。');
      setPhase('error');
    }
    return () => { cancelled = true; };
  }, [postId, reloadToken]);

  async function onSubmit(event) {
    event.preventDefault();
    if (busy || hasPendingMediaUpload(mediaItems)) return;
    const textError = String(textContent || '').length > SOCIAL_TEXT_MAX
      ? composeChrome.validation.textTooLong(SOCIAL_TEXT_MAX)
      : '';
    setFieldErrors(textError ? { textContent: textError } : {});
    if (textError) return;
    const body = buildEditSocialPostBody({
      textContent,
      mediaItems,
      expectedRowVersion,
      baselineText: baseline.text,
      baselineMedia: baseline.media,
      replyPolicy,
      quotePolicy,
      baselineReplyPolicy: baseline.replyPolicy,
      baselineQuotePolicy: baseline.quotePolicy,
    });
    if (!body) {
      setFormError('沒有需要儲存的變更。');
      return;
    }
    setBusy(true);
    setFormError('');
    try {
      const saved = await socialPostApi.edit(postId, body);
      const nextVersion = saved?.rowVersion;
      if (nextVersion) setExpectedRowVersion(nextVersion);
      navigate(`/social/posts/${postId}`, { replace: true });
    } catch (cause) {
      const key = String(cause?.raw?.key || '');
      const code = cause?.raw?.code;
      if (key === 'travel.social.post.conflict' || code === 95703) {
        setFormError('貼文已被更新，請重新載入後再編輯。');
      } else {
        setFormError(resolveSafeUserVisibleError(cause, () => '無法儲存這則貼文。', 'travel.common.actionFailed'));
      }
      setBusy(false);
    }
  }

  if (phase === 'loading') {
    return (
      <main className="social-compose" aria-busy="true">
        <p aria-live="polite">載入貼文中…</p>
      </main>
    );
  }
  if (phase === 'error') {
    const canRetry = /^[1-9]\d*$/.test(postId || '');
    return (
      <main className="social-compose">
        <p className="social-compose__error" role="alert">{formError || '無法載入貼文。'}</p>
        <div className="social-compose__actions">
          {canRetry ? (
            <button
              type="button"
              data-testid="social-edit-retry"
              onClick={() => setReloadToken((n) => n + 1)}
            >
              再試一次
            </button>
          ) : null}
          <Link to={canRetry ? `/social/posts/${postId}` : '/explore?tab=social'}>
            {canRetry ? '返回貼文' : '返回探索'}
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="social-compose" aria-labelledby={headingId}>
      <header className="social-compose__header">
        <p className="social-compose__eyebrow">編輯貼文</p>
        <h1 id={headingId}>編輯貼文</h1>
        <p>圖片順序會整包送出。封面會由第一張圖片決定。</p>
        <Link to={`/social/posts/${postId}`}>返回貼文</Link>
      </header>
      <form className="social-compose__form" onSubmit={onSubmit} noValidate>
        <div className="social-compose__field">
          <label htmlFor={textId}>內容</label>
          <textarea id={textId} value={textContent} maxLength={SOCIAL_TEXT_MAX} disabled={busy} onChange={(event) => setTextContent(event.target.value)} />
          {fieldErrors.textContent ? <span role="alert">{fieldErrors.textContent}</span> : null}
        </div>
        <fieldset className="social-compose__field" disabled={busy}>
          <legend id={replyPolicyId}>誰可以回覆</legend>
          <div className="social-compose__choices" role="radiogroup" aria-labelledby={replyPolicyId}>
            {SOCIAL_COMPOSE_REPLY_POLICIES.map((value) => (
              <label key={value}>
                <input
                  type="radio"
                  name="replyPolicy"
                  value={value}
                  checked={replyPolicy === value}
                  onChange={() => setReplyPolicy(value)}
                />
                {SOCIAL_COMPOSE_REPLY_POLICY_LABELS[value]}
              </label>
            ))}
          </div>
        </fieldset>
        <fieldset className="social-compose__field" disabled={busy}>
          <legend id={quotePolicyId}>引用</legend>
          <div className="social-compose__choices" role="radiogroup" aria-labelledby={quotePolicyId}>
            {SOCIAL_COMPOSE_QUOTE_POLICIES.map((value) => (
              <label key={value}>
                <input
                  type="radio"
                  name="quotePolicy"
                  value={value}
                  checked={quotePolicy === value}
                  onChange={() => setQuotePolicy(value)}
                />
                {SOCIAL_COMPOSE_QUOTE_POLICY_LABELS[value]}
              </label>
            ))}
          </div>
        </fieldset>
        <fieldset className="social-compose__field" disabled={busy}>
          <legend>圖片</legend>
          <SocialPostMediaEditor
            items={mediaItems}
            disabled={busy}
            onChange={(next) => setMediaItems((current) => (typeof next === 'function' ? next(current) : next))}
            onError={() => setFormError('請檢查圖片後再試。')}
          />
          {fieldErrors.media ? <span role="alert">{fieldErrors.media}</span> : null}
        </fieldset>
        {formError ? <p className="social-compose__error" role="alert">{formError}</p> : null}
        <div className="social-compose__actions">
          {formError && /重新載入/.test(formError) ? (
            <button
              type="button"
              data-testid="social-edit-reload"
              disabled={busy}
              onClick={() => setReloadToken((n) => n + 1)}
            >
              重新載入
            </button>
          ) : null}
          <button type="submit" disabled={busy || hasPendingMediaUpload(mediaItems)}>儲存</button>
          <Link to={`/social/posts/${postId}`}>取消</Link>
        </div>
      </form>
    </main>
  );
}
